'use client'

import { useQuery } from '@apollo/client/react'
import { FC, useCallback } from 'react'
import styled, { useTheme } from 'styled-components'

import {
  Button,
  CircleCheck,
  EmbroideryPatch,
  ResponsiveImage,
  TypeStyleBodyNano,
  TypeStyleHeadlineExtraExtraSmall,
  TypeStyleHeadlineMedium,
  TypeStyleHeadlineSmall,
  fromMd,
  timingDefault,
  timingSlow,
  untilMd,
} from '@syconium/little-miss-figgy'

import { gql } from '../../../__generated__/graphql/catalog/gql'
import { FetchCartInterstitialVariantsQuery } from '../../../__generated__/graphql/catalog/graphql'
import {
  useCart,
  useCartDialog,
  useCartInterstitialDialog,
} from '../../_providers/CartProvider.client'
import { useTranslation } from '../../_providers/TranslationProvider.client'

import { CartInterstitialUpsells } from './CartInterstitialUpsells/CartInterstitialUpsells'
import { NextDialog, NextDialogDismiss, NextDialogProvider } from './NextDialog.client'

type CartInterstitialItem = NonNullable<
  FetchCartInterstitialVariantsQuery['productCatalogVariants']
>[number] & {
  properties: Record<string, string | undefined>
}

const fetchCartInterstitialVariants = gql(`
  query fetchCartInterstitialVariants($skus: [String!]) {
    productCatalogVariants(skus: $skus) {
      id
      sku
      group {
        id
        title
        images(first: 1) {
          url
          altText
        }
        color {
          id
          displayValue
        }
        primaryCategory {
          id
          key
        }
      }
      size {
        id
        displayValue
      }
    }
  }
`)

// NOTE: more Cart Interstitial A/B test work is slated for 2026. Please leave this in place. - Ryan B. (03/26/2026)
export const CartInterstitialAndOverlay: FC = () => {
  const theme = useTheme()
  const {
    magnolia: { general },
    cart: { interstitial },
  } = useTranslation()
  const { isOpen: isInterstitialOpen, close: closeInterstitial } = useCartInterstitialDialog()
  const { open: openMinicart } = useCartDialog()
  const { cart, lastAddedItems, status: cartStatus } = useCart()

  const onViewBagClick = useCallback(() => {
    closeInterstitial()
    openMinicart()
  }, [closeInterstitial, openMinicart])

  const { data, loading: queryLoading } = useQuery(fetchCartInterstitialVariants, {
    variables: {
      skus: lastAddedItems.map(item => item.sku),
    },
    skip: lastAddedItems.length <= 0,
  })

  const variants = data?.productCatalogVariants

  // don't render if we haven't resolved or we don't have items
  if (cartStatus !== 'resolved' || queryLoading || variants?.length === 0) {
    return null
  }

  const cartInterstitialItems: CartInterstitialItem[] = []

  lastAddedItems.forEach(item => {
    const variant = variants?.find(e => e.sku === item.sku)

    // filter out embroidery items, we don't want to display them in the interstitial
    if (variant && variant.group.primaryCategory.key !== 'EMBROIDERY') {
      cartInterstitialItems.push({
        ...variant,
        properties: item.properties,
      })
    }
  })

  const kitTitle: string | undefined =
    cartInterstitialItems.find(
      item => typeof item.properties['_kit'] === 'string' && item.properties['_kit'] !== ''
    )?.properties['_kit'] ?? undefined

  const buildItemDescriptionCopy = (
    size: string | undefined,
    color: string | undefined
  ): string | undefined => {
    if (size && color) {
      return `${size} • ${color}`
    }
    return size || color || undefined
  }

  return (
    <NextDialogProvider open={isInterstitialOpen}>
      <CartInterstitialDialog
        analyticsTitle='Added to Cart'
        variant='default'
        onClose={closeInterstitial}
        backdropTiming={timingDefault}
        dialogTiming={timingSlow}
        // override built in padding
        content={<StyledDiv />}
      >
        <CartInterstitialContent>
          <Header>
            <HeaderCopy>
              {interstitial.addedToBag}
              <HeaderIconContainer>
                <CircleCheck
                  color={theme.color.fill.background}
                  fill={theme.color.stroke.info.text.on.background}
                />
              </HeaderIconContainer>
            </HeaderCopy>
            <DismissButton aria-label={general.close} variant='black-on-gray' size='small' />
          </Header>
          {kitTitle ? <KitTitle>{kitTitle}</KitTitle> : null}
          {/* focusable so keyboard users can scroll it when it overflows */}
          <AddedItemsContainer role='region' aria-label={interstitial.addedToBag} tabIndex={0}>
            {cartInterstitialItems.map((item, index) => (
              <AddedItem key={`${item.sku}-${index}`}>
                {item.group.images?.[0]?.url ? (
                  <ItemImageWrapper>
                    <ResponsiveImage
                      alt={item.group.images[0].altText || item.group.title}
                      src={item.group.images[0].url}
                      aspectRatios={{ sm: 80 / 90, md: 80 / 90 }}
                      loading='default'
                      widths={{ unit: 'px', sm: 80, md: 80 }}
                    />
                  </ItemImageWrapper>
                ) : null}
                <ItemCopyWrapper>
                  <ItemTitle>{item.group.title}</ItemTitle>
                  {item.size?.displayValue || item.group.color?.displayValue ? (
                    <ItemDescription>
                      {buildItemDescriptionCopy(
                        item.size?.displayValue,
                        item.group.color?.displayValue
                      )}
                    </ItemDescription>
                  ) : null}
                  {item.properties['_embroideryId'] ? (
                    <ItemDescription>
                      <AlignedEmbroideryPatch />
                      {interstitial.withEmbroidery}
                    </ItemDescription>
                  ) : null}
                </ItemCopyWrapper>
              </AddedItem>
            ))}
          </AddedItemsContainer>
          <CTAContainer>
            <ViewBagButton
              aria-label={interstitial.viewBag}
              variant='secondary'
              block
              onClick={onViewBagClick}
            >{`${interstitial.viewBag} (${cart.itemCount})`}</ViewBagButton>
          </CTAContainer>
          <CartInterstitialUpsells />
        </CartInterstitialContent>
      </CartInterstitialDialog>
    </NextDialogProvider>
  )
}

// the dialog is capped to the viewport; min-height: 0 down the flex chain
// lets AddedItemsContainer be the only part that shrinks and scrolls
const CartInterstitialDialog = styled(NextDialog)`
  display: flex;
  flex-direction: column;
  max-width: none;
  margin: 0;
  background-color: ${({ theme }) => theme.color.fill.background};
  border-radius: ${({ theme }) => theme.spacing(2)};
  ${untilMd} {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    width: 100vw;
    max-height: calc(100vh - ${({ theme }) => theme.spacing(4)});
    max-height: calc(100dvh - ${({ theme }) => theme.spacing(4)});
    border-radius: ${({ theme }) => theme.spacing(2, 2, 0, 0)};
  }
  ${fromMd} {
    position: fixed;
    bottom: ${({ theme }) => theme.spacing(10)};
    right: ${({ theme }) => theme.spacing(10)};
    width: 600px;
    max-height: calc(100vh - ${({ theme }) => theme.spacing(20)});
    max-height: calc(100dvh - ${({ theme }) => theme.spacing(20)});
  }
`
const StyledDiv = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 0;
`
const CartInterstitialContent = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 0;
  max-width: 100%;
  padding: ${({ theme }) => theme.spacing(4, 4)};

  ${fromMd} {
    padding: ${({ theme }) => theme.spacing(10, 10)};
  }
`

const DismissButton = styled(NextDialogDismiss)`
  position: absolute;
  top: ${({ theme }) => theme.spacing(4)};
  right: ${({ theme }) => theme.spacing(4)};
  margin: 0;

  svg {
    min-height: 12px;
    min-width: 12px;
    height: 12px;
    width: 12px;
  }

  ${fromMd} {
    top: ${({ theme }) => theme.spacing(10)};
    right: ${({ theme }) => theme.spacing(10)};
  }
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const HeaderCopy = styled(TypeStyleHeadlineMedium)`
  display: flex;
  gap: ${({ theme }) => theme.spacing(2)};
  align-items: center;
  letter-spacing: 1px;

  ${fromMd} {
    letter-spacing: 1.5px;
    font-size: 28px;
  }
`

const HeaderIconContainer = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  width: ${({ theme }) => theme.spacing(4)};
  height: ${({ theme }) => theme.spacing(4)};

  ${fromMd} {
    width: ${({ theme }) => theme.spacing(5)};
    height: ${({ theme }) => theme.spacing(5)};
  }
`

const ITEM_IMAGE_HEIGHT = 90

const KitTitle = styled(TypeStyleHeadlineSmall)`
  display: flex;
  align-items: flex-start;
  font-size: 17px;
  letter-spacing: 0.8px;
  margin-top: ${({ theme }) => theme.spacing(5)};

  ${fromMd} {
    margin-top: ${({ theme }) => theme.spacing(6)};
  }
`

const AddedItemsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing(4)};
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  margin: ${({ theme }) => theme.spacing(5, 0)};

  ${untilMd} {
    // show 2.5 items so the cut-off third signals the list scrolls
    max-height: calc(${ITEM_IMAGE_HEIGHT * 2.5}px + ${({ theme }) => theme.spacing(8)});
    scrollbar-width: none;
    &::-webkit-scrollbar {
      display: none;
    }
  }

  ${fromMd} {
    margin: ${({ theme }) => theme.spacing(6, 0)};
  }

  ${KitTitle} + & {
    margin-top: ${({ theme }) => theme.spacing(4)};
  }
`

const AddedItem = styled.div`
  display: flex;
  align-items: flex-start;
  width: 100%;
  gap: ${({ theme }) => theme.spacing(4)};
  max-height: 170px;
`

const ItemImageWrapper = styled.div`
  border-radius: ${({ theme }) => theme.spacing(2)};
  overflow: hidden;
  height: 100%;
  width: 100%;
  max-width: 80px;
  max-height: ${ITEM_IMAGE_HEIGHT}px;
`

const ItemCopyWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  gap: ${({ theme }) => theme.spacing(1)};
`

const ItemTitle = styled(TypeStyleHeadlineExtraExtraSmall)`
  letter-spacing: 0.6px;
  font-size: 13px;
`

const ItemDescription = styled(TypeStyleBodyNano)`
  display: flex;
  align-items: center;
  font-weight: 600;
  gap: ${({ theme }) => theme.spacing(2)};
  color: ${({ theme }) => theme.color.stroke.neutral.text.tertiary.on.background};
`

// Svg viewbox is bigger than it's stroke, so it appears shifted, this is a fix
const AlignedEmbroideryPatch = styled(EmbroideryPatch)`
  margin-left: -2px;
`

const CTAContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(5)};
`

const ViewBagButton = styled(Button)`
  height: 50px;
`
